import type { UnvaluedReasonContext } from 'components/unvalued-reason';
import type { CompositeWellboreLeg } from 'components/composite-leg-lines/types';
import { TractNRIFormulaData } from 'components/tract-nri-card';
import {
  SlotNRIVerifications,
  TitleQuality,
  TitleRiskFactor,
} from 'types/api-responses';

export interface NRIFormula {
  allocationManagerPresent: boolean;
  tractNri: number;
  tractWi: number;
  participationTractGisAc: number;
  uatGisAc: number;
  uatMeasureValue: number;
  unitMeasureValue: number;
  measureUnit: string;
  unitGisAc?: number; // Only present when allocationManagerPresent is false
}

export interface SubtractNRI {
  nriCalculationId: number;
  crmSubtractId?: string;
  crmSubtractSalesforceId?: string;
  crmTractSalesforceId?: string;
  verifications?: SlotNRIVerifications;
  uat?: string;
  uatNri?: number;
  uatWi?: number;
  nriFormula?: NRIFormula | null;
  tractNriFormula?: TractNRIFormulaData | null;
  calculationMethod?: 'nra' | 'nma';
  slotName?: string; // Well name or slot ID for NRI Matrix popup
  subtractNetAcres?: number;
  subtractGrossAcres?: number;
  quarterCall?: string;
  partTractFactor?: number;
  partTractAcres?: number;
  leaseRoyaltyRate?: number;
  leaseRoyaltyType?: string;
  ptfAdjustment?: number;
  unitCoverage?: number;
  uatCoverage?: number;
  focusMdac?: number;
  executive?: number;
  bonus?: number;
  delay?: number;
  royalty?: number;
  royaltyBurdens?: number;
  wi?: number;
  orri?: number;
  orriBurdens?: number;
  interestType?: string;
  allocationValue?: number;
  allocationValueUnit?: string;
  allocationFactor?: number;
  geojson?: GeoJSON.Feature<GeoJSON.Polygon> | null;
  intersectionGeojson?: GeoJSON.Feature<GeoJSON.Polygon> | null;
}

export interface TractNRI extends UnvaluedReasonContext {
  crmTract?: string;
  /** The tract's aliases (tags), comma-joined; blank when it has none. Read when the table is read. */
  crmTractTags?: string;
  crmTractSalesforceId?: string;
  /** Whether the package holds the Tract in part, which labels it "(2 of 3 Subtracts)". */
  heldInPart?: boolean;
  /** Subtracts Held: the package's of a Tract held in part, every one of a Tract held whole. */
  subtractsHeld?: number;
  /** Subtracts in Tract: every live Subtract of the Tract. */
  subtractsInTract?: number;
  crmAccount?: string;
  crmAccountHref?: string;
  slotId?: string;
  slotIdHref?: string;
  /**
   * Display text for the Slot ID column: the well's UWI when present, else the
   * same value as `slotId`. `slotId` stays the row identity (selection/overrides/
   * lookups); only the displayed text differs (UWI for non-tract wellbore assets).
   */
  slotIdDisplay?: string;
  assetType?: 'wellbore' | 'ltd_slot' | null;
  unit?: string;
  unitHref?: string;
  /** The unit's aliases (tags), comma-joined; blank when it has none. Read when the table is read. */
  unitTags?: string;
  totalSlotNri?: number;
  totalSlotWi?: number;
  /**
   * This TRACT's share of the slot, where the row carries one (the eval3 assets
   * allocation children). `totalSlotNri`/`totalSlotWi` above are the whole slot's,
   * repeated on every tract the slot touches — so anything pricing a single tract
   * must read these instead (INS-10673).
   */
  tractNri?: number;
  tractWi?: number;
  /** NRI source — 'certified' when from a promoted Interest Certification workspace. */
  wiSource?: string;
  /** Original certification source (allocation_manager / division_order / user_entered). */
  nriCertifiedSource?: string;
  /** Source workspace ID for linking to the certification workspace. */
  nriCertifiedWorkspaceId?: number;
  /** Entity ID for linking to the entity page (System Promoted pill). */
  interestEntityId?: number;
  allocationMethod?: string;
  allocationManagerId?: number | null;
  devAreaId?: string;
  wellId?: string | null;
  tractNetAcres?: number;
  netAcreageUnits?: string;
  tractGrossAcres?: number;
  surveyArea?: string;
  stateAbbreviation?: string;
  quarterCall?: string;
  instrumentType?: string;
  interestType?: string;
  depthRange?: string;
  unitSource?: string;
  unitDevelopmentType?: string;
  // Scenario-area selection per assumption type (modeled on the unit columns)
  dpsArea?: string;
  // false when the DPS area was captured or filled for display rather than
  // scheduled; undefined when there is no DPS area.
  dpsAreaScheduled?: boolean;
  taxArea?: string;
  differentialArea?: string;
  discountRateArea?: string;
  expenseArea?: string;
  // Paired hrefs to the scenario whose area it is; undefined on an eval captured
  // before hrefs were recorded, which renders the name as plain text.
  dpsAreaHref?: string;
  taxAreaHref?: string;
  differentialAreaHref?: string;
  discountRateAreaHref?: string;
  expenseAreaHref?: string;
  wellName?: string;
  /** The well's UWI when it has one (distinct from `slotId`, a composite for LTD slots). */
  uwi?: string;
  wellStatus?: string;
  typeCurveStatus?: string;
  operator?: string;
  holeDirection?: string;
  formation?: string;
  slotNumber?: number;
  permitDate?: string;
  spudDate?: string;
  rigReleaseDate?: string;
  completionDate?: string;
  firstProductionDate?: string;
  shutInDate?: string;
  geojson?: GeoJSON.Feature<GeoJSON.Polygon> | null;
  subtracts?: SubtractNRI[];
  trueVerticalDepthFeet?: number;
  // Volume source fields (Eval 3.0)
  volumeSourceId?: string;
  /** What the Volume Source column shows: a DCA forecast's business id, else the id. */
  volumeSourceName?: string;
  volumeSourceType?: string;
  volumeSourceHref?: string;
  forecastStatus?: string;
  forecastDate?: string;
  forecastSource?: string;
  dcaScenario?: string;
  slotLength?: number;
  typeCurveLateralLength?: number;
  basin?: string;
  tier?: string;
  typeCurveAreaName?: string;
  typeCurveVerificationStatus?: string;
  typeCurveLastVerifiedBy?: string;
  typeCurveLastVerifiedDate?: string;
  /**
   * A composite wellbore (a U-turn / S / zig-zag well read as one slot): its id, name and legs,
   * each with the formation and curve that price it. Absent on every other row. The Formation,
   * Volume Source and Forecast Status cells list the legs one line each; a Volume Source
   * override on a leg is keyed on the leg's own `slotId`.
   */
  compositeWellboreId?: number;
  compositeWellboreName?: string;
  compositeWellboreLegs?: CompositeWellboreLeg[];
  // AFE source fields (Eval 3.0)
  afeSourceId?: string;
  afeSourceHref?: string;
  afeWellName?: string;
  slotContingenciesCosts?: number;
  contingenciesCostsDate?: string;
  slotFacilitiesCosts?: number;
  facilitiesCostsDate?: string;
  slotDrillingCosts?: number;
  drillingCostsDate?: string;
  slotCompletionCosts?: number;
  completionCostsDate?: string;
  slotOtherCosts?: number;
  otherCostsDate?: string;
  // Valuation columns
  valued?: boolean;
  unvaluedReason?: string;
  // Export-only columns (not displayed in table)
  nriVerified?: string;
  wiVerified?: string;
  // Asset risk columns (tract-level worst-case across subtracts)
  titleQuality?: TitleQuality | null;
  titleRiskFactor?: TitleRiskFactor | null;
  // Per-row pricing columns (Eval 3.0). Broker uses `*Excluded`; SIM uses
  // `*Included`.
  targetNpvExcluded?: number;
  maxNpvExcluded?: number;
  targetNpvIncluded?: number;
  maxNpvIncluded?: number;
}

export type TractNRIColumns = Exclude<
  keyof TractNRI,
  | 'subtracts'
  | 'crmTractSalesforceId'
  | 'crmAccountHref'
  | 'slotIdHref'
  | 'unitHref'
  | 'allocationManagerId'
  | 'devAreaId'
  | 'wellId'
  | 'nriVerified'
  | 'wiVerified'
  | 'volumeSourceType' // Used internally for edit functionality, not displayed as column
  | 'volumeSourceHref' // Used internally for linking, not displayed as column
  | 'forecastSource' // Used internally for DCA display formatting, not displayed as column
  | 'volumeSourceName' // Displayed inside the volumeSourceId column
  | 'compositeWellboreId' // Keys a leg override's re-pricing, not a column
  | 'compositeWellboreName' // The well's stored name is its slotId; not a column of its own
  | 'compositeWellboreLegs' // Listed inside the formation / volumeSourceId / forecastStatus cells, not a column
  | 'afeSourceHref' // Used internally for linking, not displayed as column
>;

export type SubtractNRIColumns = Exclude<
  keyof SubtractNRI,
  | 'nriCalculationId'
  | 'crmSubtractSalesforceId'
  | 'crmTractSalesforceId'
  | 'verifications'
  | 'nriFormula'
  | 'tractNriFormula'
  | 'calculationMethod'
  | 'slotName'
  | 'allocationValue'
  | 'allocationValueUnit'
  | 'allocationFactor'
  | 'geojson'
  | 'intersectionGeojson'
>;

export type CalculationType = 'NRI' | 'WI';

export interface UatValueItem {
  name: string;
  value: number;
}

export interface UnitCalculation {
  slotName: string;
  uatItems: UatValueItem[];
  calculatedValue: number;
  calculationType: CalculationType;
}

// Keep for backwards compatibility
export type UatNriItem = UatValueItem;
export type UnitNRICalculation = Omit<UnitCalculation, 'calculationType'>;

/**
 * Configuration for volume source editing and display in the NRI table.
 * Consolidates all volume source related props.
 */
export interface VolumeSourceConfig {
  /** Callback for editing volume source - opens the edit modal */
  onEdit?: (slotId: string) => void;
  /** Disables the volume source edit button (when using Eval 2.0) */
  isEditDisabled?: boolean;
  /** Slot IDs that have volume source overrides (for highlighting) */
  overriddenSlotIds?: string[];
  /** Map of slotId to overridden volumeSourceId (for display) */
  overriddenVolumeSourceIds?: Record<string, string>;
  /** Map of slotId to overridden volumeSourceHref (for linking) */
  overriddenVolumeSourceHrefs?: Record<string, string>;
  /** Map of slotId to overridden DCA scenario name (for DCA overrides) */
  overriddenDcaScenarios?: Record<string, string>;
}

/**
 * Configuration for AFE source editing and display in the NRI table.
 * Consolidates all AFE source related props.
 */
export type CostDateField =
  | 'contingenciesCostsDate'
  | 'facilitiesCostsDate'
  | 'drillingCostsDate'
  | 'completionCostsDate'
  | 'otherCostsDate';

export type SlotDateField = 'permitDate' | 'spudDate' | 'firstProductionDate';

export interface AfeSourceConfig {
  /** Callback for editing AFE source - opens the edit modal */
  onEditAfeSource?: (slotId: string) => void;
  /** Callback for editing a cost date - opens the date picker modal */
  onEditCostDate?: (slotId: string, dateField: CostDateField) => void;
  /** Callback for editing a slot date (permit/spud/first production) */
  onEditSlotDate?: (slotId: string, dateField: SlotDateField) => void;
  /** Disables the AFE source edit button */
  isEditDisabled?: boolean;
  /** Slot IDs that have AFE source overrides (drives the persistent override checkmark) */
  overriddenAfeSlotIds?: string[];
  /** Slot IDs whose AFE override changed since the last eval run (drives the
   *  "not yet re-run" blue cell highlight; clears once the eval is re-run). */
  changedSinceRunAfeSlotIds?: string[];
  /** Map of slotId to overridden afeSourceId (for display) */
  overriddenAfeSourceIds?: Record<string, string>;
  /** Map of slotId to overridden afeSourceHref (for linking) */
  overriddenAfeSourceHrefs?: Record<string, string>;
  /** Map of slotId to overridden cost dates */
  overriddenCostDates?: Record<string, Partial<Record<CostDateField, string>>>;
  /** Map of slotId to overridden slot dates */
  overriddenSlotDates?: Record<
    string,
    Partial<Record<SlotDateField, string | null>>
  >;
}

export type AcreageUnit = 'ac';
export type FeetUnit = 'ft';

export type UatUnit = FeetUnit | AcreageUnit;

export interface UATCalculation {
  tractValue: number;
  uat: number;
  uatUnit: UatUnit;
  length: number;
  lengthUnit: UatUnit;
  participationTract: number;
  participationTractUnit: AcreageUnit;
  uatValueAc: number;
  uatValueAcUnit: AcreageUnit;
  result: number;
  calculationType: CalculationType;
  hasAllocationManager?: boolean;
  tractNriFormula?: TractNRIFormulaData | null;
  tractNriCalculationMethod?: 'nra' | 'nma';
  instrumentType?: string;
}

// Keep for backwards compatibility
export type UATNRICalculation = Omit<
  UATCalculation,
  'calculationType' | 'tractValue'
> & {
  tractNri: number;
};

// Default column arrays containing all columns (order matches column config in utils.tsx)
export const DEFAULT_TRACT_COLUMNS: TractNRIColumns[] = [
  'crmTract',
  'crmAccount',
  'slotId',
  'unit',
  'targetNpvExcluded',
  'maxNpvExcluded',
  'targetNpvIncluded',
  'maxNpvIncluded',
  'totalSlotNri',
  'totalSlotWi',
  'allocationMethod',
  'tractNetAcres',
  'netAcreageUnits',
  'tractGrossAcres',
  'surveyArea',
  'stateAbbreviation',
  'quarterCall',
  'instrumentType',
  'interestType',
  'depthRange',
  'unitSource',
  'unitDevelopmentType',
  'wellName',
  'wellStatus',
  'typeCurveStatus',
  'operator',
  'holeDirection',
  'formation',
  'slotNumber',
  'permitDate',
  'spudDate',
  'rigReleaseDate',
  'completionDate',
  'firstProductionDate',
  'shutInDate',
];

/**
 * DEFAULT_TRACT_COLUMNS with the tract's and the unit's tags beside the tract and the unit they tag
 * (INS-13689). For the tables whose data carries tags (the entity page); the default list stays as it
 * was for the legacy evaluation-result tables.
 */
export const TAGGED_TRACT_COLUMNS: TractNRIColumns[] =
  DEFAULT_TRACT_COLUMNS.flatMap((column): TractNRIColumns[] => {
    if (column === 'crmTract') return [column, 'crmTractTags'];
    if (column === 'unit') return [column, 'unitTags'];
    return [column];
  });

export const DEFAULT_SUBTRACT_COLUMNS: SubtractNRIColumns[] = [
  'crmSubtractId',
  'uat',
  'uatNri',
  'uatWi',
  'subtractNetAcres',
  'subtractGrossAcres',
  'quarterCall',
  'partTractFactor',
  'partTractAcres',
  'leaseRoyaltyRate',
  'leaseRoyaltyType',
  'ptfAdjustment',
  'unitCoverage',
  'uatCoverage',
  'focusMdac',
  'executive',
  'bonus',
  'delay',
  'royalty',
  'royaltyBurdens',
  'wi',
  'orri',
  'orriBurdens',
  'interestType',
];
